import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { toastError } from "../../shared/services/errors/erros-handler";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
} from "@mui/material";
import InputSetores from "../../shared/components/formulario/inputSetores/inputSetor";
import InputPlanoDeContas from "../../shared/components/formulario/inputPlanoDeContas/inputPlanoDeContas";
import { MovementTypeEnum } from "../financeiro/schema/gerar-movimentacao-obra.schema";
import { SelectNfsEmpresa } from "../../shared/components/formulario/input-select-nfs/selectNfs";
import { useEffect, useState } from "react";
import InputData from "../../shared/components/formulario/input-data/inputData";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import {
  MovementOriginEnum,
  PeriodicityEnum,
} from "../../shared/utils/enum/enum";
import { SelectObra } from "../../shared/components/formulario/input-select-obra/selectObra";
import { SelectCliente } from "../../shared/components/formulario/input-select-cliente/selectCliente";
import { useSectorById } from "../../shared/components/formulario/inputSetores/use-sector";
import { formatDate } from "../../shared/utils";
import { useMutation } from "@tanstack/react-query";
import { EditarContasAReceberFormData, editarContasAReceberSchema } from "./schema/editar-contas-a-receber.schema";
import { contasAReceberService } from "./service/criar-contas-a-receber.service";

export function EditarContasReceber() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const methods = useForm<EditarContasAReceberFormData>({
    resolver: yupResolver(editarContasAReceberSchema),
    defaultValues: {},
  });

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
    watch,
  } = methods;

  const [isRelatedToNfs, setIsRelatedToNfs] = useState(false);

  // Carregamento dos dados da conta
  useEffect(() => {
    if (id) {
      contasAReceberService
        .detalhar(id)
        .then((conta) => {
          reset({
            ...conta,
            idSector: conta.idSector ?? "",
            chartOfAccountsId: conta.chartOfAccountsId ?? "",
            description: conta.description ?? "",
            totalValue: Number(conta.totalValue) ?? 0,
            purchaseDate: conta.purchaseDate
              ? formatDate(conta.purchaseDate)
              : "",
            numberOfInstallments: conta.numberOfInstallments ?? 1,
            clientId: conta.clientId ?? "",
            workId: conta.workId ?? "",
            periodicity: conta.periodicity ?? "",
            idNfs: conta.idNfs ?? "",
            anticipateAccount: !!conta.anticipateAccount,
            newTotalValue: conta.newTotalValue
              ? Number(conta.newTotalValue)
              : undefined,
          });
        })
        .catch((error) => {
          toastError(error);
        });
    }
    // eslint-disable-next-line
  }, [id, reset]);

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: EditarContasAReceberFormData) => {
      // Converte campos vazios para null
      const payload = {
        ...data,
        clientId: !data.clientId ? null : data.clientId,
        workId: !data.workId ? null : data.workId,
        idNfs: !data.idNfs ? null : data.idNfs,
      };
      const result = await contasAReceberService.editar(id || "", payload as any);
      return result;
    },
    onSuccess: () => {
      toast.success("Conta editada com sucesso!");
      navigate("/financeiro/contas-a-receber");
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  // Watchers
  const sector = watch("idSector");
  const idCliente = watch("clientId");
  const idObra = watch("workId");
  const setorSelecionado = useSectorById(sector);
  const anticipateAccount = watch("anticipateAccount");
  const idNfsValue = watch("idNfs");

  return (
    <BaseLayout title="Editar Receita">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoVoltar
        aoClicarBotaoSalvar={handleSubmit((formData) => mutate(formData))}
        aoClicarBotaoVoltar={() => navigate("/financeiro/contas-a-receber")}
      />

      <Box
        component="form"
        onSubmit={handleSubmit((formData) => {
          mutate(formData);
        })}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px )"
      >
        <Grid container display="flex" spacing={2}>
          <Grid item xs={12} md={4}>
            <InputSetores
              name="idSector"
              label="Setor de Origem"
              methods={methods}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputPlanoDeContas
              name="chartOfAccountsId"
              label="Plano de Contas"
              tiPosMovimentacao={MovementTypeEnum.INCOME || ""}
              methods={methods}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descrição"
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputMascarado
              name="totalValue"
              methods={methods}
              label="Valor Total"
              mask={mascaraMoedaReal}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputData
              name="purchaseDate"
              methods={methods}
              label="Data de Vencimento"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="numberOfInstallments"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Quantidade de Parcelas"
                  type="number"
                  error={!!errors.numberOfInstallments}
                  helperText={errors.numberOfInstallments?.message}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="periodicity"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Periodicidade"
                  error={!!errors.periodicity}
                  helperText={errors.periodicity?.message}
                  fullWidth
                  size="small"
                  value={field.value ?? ""}
                >
                  <MenuItem value="">Selecione</MenuItem>
                  <MenuItem value={PeriodicityEnum.MONTHLY}>Mensal</MenuItem>
                  <MenuItem value={PeriodicityEnum.BIWEEKLY}>
                    Bimestral
                  </MenuItem>
                  <MenuItem value={PeriodicityEnum.WEEKLY}>Semestral</MenuItem>
                </TextField>
              )}
            />
          </Grid>
        </Grid>
        {setorSelecionado?.origin === MovementOriginEnum.WORK && (
          <Box display="flex" mt={3} gap={2}>
            <Controller
              name="clientId"
              control={control}
              render={({ field }) => (
                <SelectCliente
                  value={field.value || ""}
                  onChange={(id, name) => {
                    field.onChange(id);
                  }}
                  required={
                    setorSelecionado?.origin === MovementOriginEnum.WORK
                  }
                />
              )}
            />
            <Controller
              name="workId"
              control={control}
              render={({ field }) => (
                <SelectObra
                  value={field.value || ""}
                  onChange={(id, name) => {
                    field.onChange(id);
                  }}
                  idCliente={idCliente || ""}
                  required={
                    setorSelecionado?.origin === MovementOriginEnum.WORK
                  }
                />
              )}
            />
          </Box>
        )}

        <Box mt={3}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              Esta movimentação está relacionada a uma entrada de nota fiscal?
            </FormLabel>
            <RadioGroup
              row
              value={isRelatedToNfs ? "sim" : "nao"}
              onChange={(e) => setIsRelatedToNfs(e.target.value === "sim")}
            >
              <FormControlLabel value="sim" control={<Radio />} label="Sim" />
              <FormControlLabel value="nao" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>
          {isRelatedToNfs && (
            <Controller
              name="idNfs"
              control={control}
              render={({ field }) => (
                <SelectNfsEmpresa
                  name="idNfs"
                  methods={methods}
                  idWork={idObra || ""}
                  label="Selecione uma Nota Fiscal"
                  required={true}
                />
              )}
            />
          )}
        </Box>
        {/* Campos para antecipação */}
        <Box mt={3}>
          <FormControlLabel
            control={
              <Controller
                name="anticipateAccount"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={!!field.value}
                    color="primary"
                  />
                )}
              />
            }
            label="Antecipar parcelas deste documento?"
          />
        </Box>
        {anticipateAccount && (
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={4}>
              <Controller
                name="newDateDoc"
                control={control}
                render={({ field }) => (
                  <InputData
                    {...field}
                    name="newDateDoc"
                    methods={methods}
                    label="Nova data do boleto de antecipação"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="newTotalValue"
                control={control}
                render={({ field }) => (
                  <InputMascarado
                    {...field}
                    name="newTotalValue"
                    methods={methods}
                    label="Novo valor do boleto de antecipação"
                    mask={mascaraMoedaReal}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}

        <Box display="flex" justifyContent="flex-end" mt={4}>
          <button type="submit" style={{ display: "none" }} />
        </Box>
      </Box>
    </BaseLayout>
  );
}
